import { AIReviewStatisticsItem } from 'services/api/adminStatistics';

interface ExportData {
  statistics: AIReviewStatisticsItem[];
  startDate: string;
  endDate: string;
  unit: string;
}

/**
 * 통계 데이터를 CSV로 다운로드 (피벗 테이블 형식)
 */
export function exportStatisticsToCSV(data: ExportData): void {
  const { statistics, startDate, endDate, unit } = data;

  if (!statistics || statistics.length === 0) {
    return;
  }

  // 1. 모든 기간 추출 (정렬)
  const periodSet = new Set<string>();
  statistics.forEach((item) => {
    const periodLabel = `${item.periodStartDate}~${item.periodEndDate}`;
    periodSet.add(periodLabel);
  });
  const periods = Array.from(periodSet).sort();

  // 2. 복합 키 생성 함수 (차트와 동일한 로직)
  const getRowKey = (item: AIReviewStatisticsItem): string => {
    let key = item.premiumRole;
    if (item.userId) key += `|${item.userId}`;
    if (item.promptType) key += `|${item.promptType}`;
    if (item.status !== undefined) key += `|${item.status}`;
    return key;
  };

  // 3. 데이터를 행 키별로 그룹화
  const rowMap = new Map<string, {
    premiumRole: string;
    status: string;
    promptType: string;
    userId: string;
    userName: string;
    licenseNumber: string;
    periods: Map<string, number>;
  }>();

  statistics.forEach((item) => {
    const rowKey = getRowKey(item);
    const periodLabel = `${item.periodStartDate}~${item.periodEndDate}`;

    if (!rowMap.has(rowKey)) {
      rowMap.set(rowKey, {
        premiumRole: item.premiumRole,
        status: item.status !== undefined ? item.status.toString() : '',
        promptType: item.promptType || '',
        userId: item.userId || '',
        userName: item.userName || '',
        licenseNumber: item.licenseNumber || '',
        periods: new Map(),
      });
    }

    const row = rowMap.get(rowKey)!;
    row.periods.set(periodLabel, item.count);
  });

  // 4. CSV 헤더 생성 (마지막에 합계 열 추가)
  const headers = [
    'Premium Role',
    'Status',
    'Prompt Type',
    'User ID',
    '사용자 이름',
    '면허번호',
    ...periods,
    '합계',
  ];

  // 5. CSV 행 생성 (각 행에 합계 열 추가)
  const rows = Array.from(rowMap.values()).map((row) => {
    const periodValues = periods.map((period) => row.periods.get(period) || 0);
    const rowTotal = periodValues.reduce((acc, val) => acc + val, 0);
    return [
      row.premiumRole,
      row.status,
      row.promptType,
      row.userId,
      row.userName,
      row.licenseNumber,
      ...periodValues.map((v) => v.toString()),
      rowTotal.toString(),
    ];
  });

  // 6. 합계 행 생성 (기간별 합계 + 총 합계)
  const periodTotals = periods.map((period) => {
    return Array.from(rowMap.values()).reduce((acc, row) => {
      return acc + (row.periods.get(period) || 0);
    }, 0);
  });
  const grandTotal = periodTotals.reduce((acc, val) => acc + val, 0);
  const totalRow = [
    '합계',
    '',
    '',
    '',
    '',
    '',
    ...periodTotals.map((v) => v.toString()),
    grandTotal.toString(),
  ];
  rows.push(totalRow);

  // CSV 문자열 생성 (쉼표와 따옴표 이스케이프 처리)
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row
        .map((cell) => {
          // 쉼표, 줄바꿈, 따옴표가 포함된 경우 따옴표로 감싸기
          const cellStr = String(cell);
          if (
            cellStr.includes(',') ||
            cellStr.includes('\n') ||
            cellStr.includes('"')
          ) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(',')
    ),
  ].join('\n');

  // BOM 추가 (Excel 한글 깨짐 방지)
  const blob = new Blob(['\uFEFF' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  // 다운로드
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    `AI리뷰_통계_${startDate}_${endDate}_${unit}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
